import { useState, useEffect, useCallback } from 'react';
import { API, useAuth } from '../App';
import axios from 'axios';

// Convert base64 to Uint8Array for applicationServerKey
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const { token, user } = useAuth();
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [permission, setPermission] = useState('default');
  const [vapidKey, setVapidKey] = useState(null);

  // Check if push notifications are supported
  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
    }
  }, []);

  // Fetch VAPID key and check subscription status
  useEffect(() => {
    const init = async () => {
      if (!isSupported) {
        setIsLoading(false);
        return;
      }

      try {
        // Get VAPID key
        const vapidResponse = await axios.get(`${API}/push/vapid-key`);
        if (vapidResponse.data.configured && vapidResponse.data.public_key) {
          setVapidKey(vapidResponse.data.public_key);
        }

        // Check subscription status if user is logged in
        if (token) {
          const statusResponse = await axios.get(`${API}/push/status`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setIsSubscribed(statusResponse.data.subscribed);
        }
      } catch (error) {
        console.error('Push init error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [isSupported, token]);

  // Register service worker
  const registerServiceWorker = useCallback(async () => {
    if (!isSupported) return null;

    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      if (process.env.NODE_ENV === "development") console.log('Service Worker registered:', registration);
      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return null;
    }
  }, [isSupported]);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!isSupported || !vapidKey || !token) {
      console.error('Cannot subscribe: not supported, no VAPID key, or not logged in');
      return false;
    }

    try {
      // Request permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      
      if (perm !== 'granted') {
        if (process.env.NODE_ENV === "development") console.log('Notification permission denied');
        return false;
      }

      // Register service worker
      const registration = await registerServiceWorker();
      if (!registration) {
        console.error('No service worker registration');
        return false;
      }

      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey)
      });

      // Send subscription to backend
      const subscriptionData = subscription.toJSON();
      await axios.post(`${API}/push/subscribe`, {
        endpoint: subscriptionData.endpoint,
        keys: subscriptionData.keys
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setIsSubscribed(true);
      if (process.env.NODE_ENV === "development") console.log('Push subscription successful');
      return true;
    } catch (error) {
      console.error('Push subscription error:', error);
      return false;
    }
  }, [isSupported, vapidKey, token, registerServiceWorker]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    if (!isSupported || !token) return false;

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
      }

      // Notify backend
      await axios.post(`${API}/push/unsubscribe`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setIsSubscribed(false);
      if (process.env.NODE_ENV === "development") console.log('Push unsubscription successful');
      return true;
    } catch (error) {
      console.error('Push unsubscription error:', error);
      return false;
    }
  }, [isSupported, token]);

  return {
    isSupported,
    isSubscribed,
    isLoading,
    permission,
    vapidConfigured: !!vapidKey,
    subscribe,
    unsubscribe
  };
}

export default usePushNotifications;
